import React from 'react';
import Icon from '../../../components/AppIcon';

const PricingBreakdown = ({ booking }) => {
  const baseAmount = booking?.baseAmount || booking?.totalAmount * 0.8;
  const taxesAndFees = booking?.taxesAndFees || booking?.totalAmount * 0.12;
  const serviceFee = booking?.serviceFee || booking?.totalAmount * 0.08;
  const totalAmount = booking?.totalAmount || baseAmount + taxesAndFees + serviceFee;

  const priceItems = [
    {
      label: booking?.type === 'accommodation' ? `₱${(baseAmount / (booking?.nights || 1))?.toLocaleString('en-PH', { minimumFractionDigits: 2 })} x ${booking?.nights || 1} nights` : 'Base Price',
      amount: baseAmount,
      icon: 'DollarSign',
      color: 'text-primary'
    },
    {
      label: 'Taxes & Fees',
      amount: taxesAndFees,
      icon: 'Receipt',
      color: 'text-secondary'
    },
    {
      label: 'Service Fee',
      amount: serviceFee,
      icon: 'CreditCard',
      color: 'text-accent'
    }
  ];

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-lg sticky top-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-secondary p-6 text-white">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Wallet" size={24} />
          <h3 className="text-xl font-bold">Price Breakdown</h3>
        </div>
        <div className="mt-4">
          <p className="text-sm text-white/80 mb-1">Total Amount</p>
          <p className="text-4xl font-bold">
            ₱{totalAmount?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      {/* Price Items */}
      <div className="p-6 space-y-4">
        {priceItems?.map((item, index) => {
          const percentage = (item?.amount / totalAmount) * 100;
          
          return (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name={item?.icon} size={16} className={item?.color} />
                  <span className="text-sm text-foreground">{item?.label}</span>
                </div>
                <span className="text-sm font-semibold text-foreground">
                  ₱{item?.amount?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-right">
                {percentage?.toFixed(1)}% of total
              </p>
            </div>
          );
        })}

        {/* Total */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-base font-bold text-foreground">Total (PHP)</span>
            <span className="text-2xl font-bold text-primary">
              ₱{totalAmount?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {/* Per Guest Breakdown */}
        {booking?.guests > 1 && (
          <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Users" size={16} className="text-accent" />
                <span className="text-sm text-foreground">Per Guest ({booking?.guests} guests)</span>
              </div>
              <span className="text-sm font-semibold text-foreground">
                ₱{(totalAmount / booking?.guests)?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="p-4 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">
                All prices are in Philippine Peso (₱). Final amount includes all applicable taxes and fees.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingBreakdown;